import { Checkbox } from "@chakra-ui/react";
import NoData from "components/noData";
import { useId, useRef } from "react";
import type { ReactNode } from "react";

export type HeaderObject = {
  title: ReactNode;
  headerKey: string;
};

interface SimpleTableProps {
  header: HeaderObject[];
  data: any[];
  hasIndex?: boolean;
  page?: number;
  count?: number;
  isCheckTable?: boolean;
  selectedItems?: any[];
  setSelectedItems?: React.Dispatch<any[]>;
  extra?: string;
}

const SimpleTable = (props: SimpleTableProps) => {
  const {
    header: tableHeader,
    hasIndex,
    page,
    count,
    data: tableData,
    isCheckTable,
    selectedItems = [],
    setSelectedItems,
    extra,
  } = props;

  const uniqId = useId();
  const lastCheckedIndex = useRef<number | null>(null); // keep track of last clicked index

  const allChecked =
    selectedItems.length === tableData.length && selectedItems.length !== 0;

  const isIndeterminate = selectedItems.length > 0 && !allChecked;

  const selectAllClicked = () => {
    let all: string[] = [];
    tableData.forEach((rowObject: any) => all.push(rowObject.counterSerial));
    allChecked ? setSelectedItems([]) : setSelectedItems(all);
  };

  const rowIsChecked = (rowObject: any) => {
    return selectedItems.includes(rowObject.counterSerial);
  };

  const rowClicked = (rowObject: any, idx: number, shiftKey: boolean) => {
    let newSelected = [...selectedItems];

    if (shiftKey && lastCheckedIndex.current !== null) {
      // figure out range
      const start = Math.min(lastCheckedIndex.current, idx);
      const end = Math.max(lastCheckedIndex.current, idx);

      const rangeItems = tableData
        .slice(start, end + 1)
        .map((row) => row.counterSerial);

      // merge unique items
      const merged = new Set([...newSelected, ...rangeItems]);
      newSelected = Array.from(merged);
      setSelectedItems(newSelected);
    } else {
      // normal toggle
      if (newSelected.includes(rowObject.counterSerial)) {
        newSelected = newSelected.filter(
          (item) => item !== rowObject.counterSerial
        );
      } else {
        newSelected.push(rowObject.counterSerial);
      }
      setSelectedItems(newSelected);

      // remember last clicked
      lastCheckedIndex.current = idx;
    }
  };

  return (
    <div
      className={`mt-8 ${extra} px-2 pb-4 md:overflow-x-visible md:px-4`}
      tabIndex={0}
      style={{ outline: "none", maxHeight: "50vh" }}
    >
      {tableData.length > 0 ? (
        <>
          {/* Desktop / Tablet Table */}
          <table className="relative hidden w-full table-auto border-separate md:table">
            <thead className="bg-white dark:!bg-navy-900">
              <tr className="sticky top-0 !z-[1000]">
                {isCheckTable && (
                  <th className="sticky top-0 border-b-[1px] border-gray-200 bg-white pb-2 pr-2 pt-4 text-center dark:!bg-navy-700">
                    <Checkbox
                      isChecked={allChecked}
                      isIndeterminate={isIndeterminate}
                      onChange={selectAllClicked}
                    />
                  </th>
                )}
                {hasIndex && (
                  <th className="sticky top-0 border-b-[1px] border-gray-200 bg-white pb-2 pr-2 pt-4 text-center dark:!bg-navy-700">
                    #
                  </th>
                )}
                {tableHeader.map((item, id) => (
                  <th
                    key={`${id}-${uniqId}-${id}`}
                    className="sticky top-0 border-b-[1px] border-gray-200 bg-white pb-2 pr-2 pt-4 text-center md:text-sm 4xl:text-lg dark:!bg-navy-700"
                  >
                    {item.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((data, idx) => (
                <tr
                  key={`${uniqId}-${idx}`}
                  className="hover:bg-gray-50 sm:!text-xs md:!text-sm 4xl:!text-lg dark:hover:bg-navy-700"
                >
                  {isCheckTable && (
                    <td className="mx-auto w-10 border-white/0 py-3 pr-2 text-center">
                      <Checkbox
                        isChecked={rowIsChecked(data)}
                        onChange={(e) =>
                          rowClicked(
                            data,
                            idx,
                            (e.nativeEvent as MouseEvent).shiftKey
                          )
                        }
                      />
                    </td>
                  )}
                  {hasIndex && (
                    <td className="mx-auto w-10 border-white/0 py-3 pr-2 text-center">
                      {page && count ? (page - 1) * count + idx + 1 : idx + 1}
                    </td>
                  )}
                  {tableHeader.map((d, i) => (
                    <td
                      key={`${i}-${uniqId}`}
                      className="mx-auto whitespace-normal break-words border-white/0 py-3 pr-4 text-center"
                    >
                      {data[d.headerKey]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile Card View */}
          <div className="space-y-4 md:hidden">
            {tableData.map((data, idx) => (
              <div
                key={`${uniqId}-card-${idx}`}
                className="rounded-xl border bg-white p-3 shadow-sm dark:bg-navy-800"
              >
                {hasIndex && (
                  <div className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                    #{page && count ? (page - 1) * count + idx + 1 : idx + 1}
                  </div>
                )}
                {tableHeader.map((d, i) => (
                  <div key={i} className="flex justify-between py-1 text-sm">
                    <span className="font-semibold text-gray-500 dark:text-gray-300">
                      {d.title}
                    </span>
                    <span className="text-left">{data[d.headerKey]}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      ) : (
        <NoData />
      )}
    </div>
  );
};

export default SimpleTable;
